import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true
});

// api.defaults.withXSRFToken = true;

api.interceptors.request.use(
  (config) => {
    // An explicit per-request Authorization header (e.g. the 2FA challenge token) wins
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ── Token refresh helpers ──────────────────────────────────────────────
let isRefreshing = false;
let refreshSubscribers = [];
let refreshTimer = null;

const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (cb) => {
  refreshSubscribers.push(cb);
};

/**
 * Persist the token and schedule a proactive refresh at 75 % of its
 * lifetime so the user never hits an expired-token 401 during normal use.
 */
export const storeTokenAndScheduleRefresh = (token, expiresInMinutes) => {
  localStorage.setItem('token', token);

  if (refreshTimer) clearTimeout(refreshTimer);

  if (expiresInMinutes) {
    const refreshMs = expiresInMinutes * 60 * 1000 * 0.75;
    refreshTimer = setTimeout(() => doRefresh(), refreshMs);
  }
};

const doRefresh = async () => {
  if (isRefreshing) return;
  isRefreshing = true;

  try {
    const { data } = await api.post('/refresh-token');
    storeTokenAndScheduleRefresh(data.token, data.expires_in);
    onTokenRefreshed(data.token);
  } catch {
    // Proactive refresh failed – the 401 interceptor will handle it
    // when the next request is made with the expired token.
  } finally {
    isRefreshing = false;
  }
};

// ── Response interceptor (fallback for expired tokens) ────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      const skipRefreshUrls = ['/login', '/refresh-token', '/register'];
      const shouldSkip = skipRefreshUrls.some((url) => originalRequest?.url?.includes(url));

      if (shouldSkip || originalRequest._retry) {
        if (!originalRequest?.url?.includes('/login')) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await api.post('/refresh-token');
        storeTokenAndScheduleRefresh(data.token, data.expires_in);
        onTokenRefreshed(data.token);

        originalRequest.headers.Authorization = `Bearer ${data.token}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// "Don't ask for a 2FA code on this device" token. Deliberately kept across logouts;
// the server stores only a hash and expires it after TWO_FACTOR_TRUSTED_DEVICE_DAYS.
const TRUSTED_DEVICE_KEY = 'trusted_device_token';
export const getTrustedDeviceToken = () => localStorage.getItem(TRUSTED_DEVICE_KEY);
export const rememberTrustedDevice = (data) => {
  if (data?.trusted_device_token) localStorage.setItem(TRUSTED_DEVICE_KEY, data.trusted_device_token);
};
export const forgetTrustedDevice = () => localStorage.removeItem(TRUSTED_DEVICE_KEY);

export const authService = {
  register: async (data) => {
    const response = await api.post('/register', data);
    return response.data;
  },

  login: async (credentials) => {
    const trustedDeviceToken = getTrustedDeviceToken();
    const response = await api.post(
      '/login',
      trustedDeviceToken ? { ...credentials, trusted_device_token: trustedDeviceToken } : credentials
    );
    if (response.data.token) {
      storeTokenAndScheduleRefresh(response.data.token, response.data.expires_in);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: async () => {
    try {
      await api.post('/logout');
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  getCurrentUser: async () => {
    const response = await api.get('/me');
    localStorage.setItem('user', JSON.stringify(response.data));
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await api.put('/profile', data);
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  forgotPassword: async (email, extra = {}) => {
    const response = await api.post('/forgot-password', { email, ...extra });
    return response.data;
  },

  resetPassword: async (data) => {
    const response = await api.post('/reset-password', data);
    return response.data;
  },

  impersonateUser: async (userId) => {
    const response = await api.post(`/admin/users/${userId}/impersonate`);
    return response.data;
  },

  stopImpersonation: async () => {
    const response = await api.post('/admin/stop-impersonation');
    return response.data;
  }
};

// Helper function to get stored user
export const getStoredUser = () => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

// Helper function to check if user is authenticated
export const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

// Admin Account Service (for currency accounts)
export const adminAccountService = {
  getAll: async (params) => {
    const response = await api.get('/admin/admin-accounts', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/admin-accounts/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/admin-accounts', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/admin-accounts/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/admin-accounts/${id}`);
    return response.data;
  }
};

// Client Invoice Service (admin-created revenue invoices)
export const clientInvoiceService = {
  getAll: async (params) => {
    const response = await api.get('/admin/client-invoices', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/client-invoices/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/client-invoices', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/client-invoices/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/client-invoices/${id}`);
    return response.data;
  },

  send: async (id) => {
    const response = await api.post(`/admin/client-invoices/${id}/send`);
    return response.data;
  },

  markAsPaid: async (id) => {
    const response = await api.post(`/admin/client-invoices/${id}/mark-as-paid`);
    return response.data;
  },

  getStatistics: async (params) => {
    const response = await api.get('/admin/client-invoices/statistics', { params });
    return response.data;
  },

  generateLineItems: async (projectId, billingPeriodStart, billingPeriodEnd) => {
    const response = await api.post('/admin/client-invoices/generate-line-items', {
      project_id: projectId,
      billing_period_start: billingPeriodStart,
      billing_period_end: billingPeriodEnd
    });
    return response.data;
  },

  getClientTimesheet: async (id) => {
    const response = await api.get(`/admin/client-invoices/${id}/export-excel`, {
      responseType: 'blob'
    });
    return response.data;
  },

  exportExcel: async (id) => {
    const response = await api.get(`/admin/client-invoices/${id}/export-excel`, {
      responseType: 'blob'
    });
    return response.data;
  },

  exportPdf: async (id) => {
    const response = await api.get(`/admin/client-invoices/${id}/export-pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

// Client Portal Invoice Service (for clients to view their invoices)
export const clientPortalInvoiceService = {
  getAll: async (params) => {
    const response = await api.get('/client/client-invoices', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/client/client-invoices/${id}`);
    return response.data;
  },

  exportPdf: async (id) => {
    const response = await api.get(`/client/client-invoices/${id}/export-pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  markAsPaid: async (id) => {
    const response = await api.post(`/client/client-invoices/${id}/mark-as-paid`);
    return response.data;
  }
};

// Payment Terms Template Service
export const paymentTermsTemplateService = {
  getAll: async () => {
    const response = await api.get('/admin/payment-terms-templates');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/payment-terms-templates/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/payment-terms-templates', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/payment-terms-templates/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/payment-terms-templates/${id}`);
    return response.data;
  }
};

// Company Details Template Service
export const companyDetailsTemplateService = {
  getAll: async () => {
    const response = await api.get('/admin/company-details-templates');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/company-details-templates/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/company-details-templates', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/company-details-templates/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/company-details-templates/${id}`);
    return response.data;
  }
};

export default api;
